// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/lib/transferPricing.ts
// 🔧 PURPOSE: L'anteprima del prezzo del transfer, con la STESSA regola del
//             channel manager (src/lib/transfer-pricing.ts) e quindi del
//             sito. Il server resta l'unico a decidere quanto si paga — il
//             checkout riprezza sempre da capo — ma il numero che l'ospite
//             legge prima di aprire la carta dev'essere esattamente quello
//             che gli verrà addebitato: un'anteprima che sbaglia è peggio di
//             nessuna anteprima.
//
//             Perché una copia e non una chiamata di preventivo: il prezzo si
//             ricalcola a ogni spunta e a ogni orario digitato, e una
//             richiesta di rete per ogni tasto renderebbe il form lento e
//             sbagliato di mezzo secondo. I dati del listino (prezzi delle
//             tratte, stazioni, fascia notturna) arrivano già dal catalogo:
//             qui si applica solo l'aritmetica.
// ============================================

import type { TransferOptions } from './guestApi';

/** Le scelte dell'ospite nel form del transfer. */
export interface TransferSelection {
  isArrival?: boolean;
  isDeparture?: boolean;
  /** Aeroporto: "YYYY-MM-DDTHH:MM". */
  arrivalDateTime?: string;
  departureDateTime?: string;
  /** Stazione: "HH:MM". */
  arrivalTime?: string;
  departureTime?: string;
  stationName?: string;
  departureStationName?: string;
  flightNumber?: string;
  departureFlightNumber?: string;
  notes?: string;
}

export interface TransferQuote {
  total: number;
  nightSurcharge: number;
  /** Tratte selezionate e con un prezzo: 0, 1 o 2. */
  trips: number;
}

/**
 * Anticipo con cui il taxi passa a prendere chi parte in aereo: la fascia
 * notturna della partenza si valuta su QUESTO orario, non sull'ora del volo.
 * Stesso valore di LEAVE_BEFORE_DEPARTURE_MIN nel channel manager.
 */
export const LEAVE_BEFORE_DEPARTURE_MIN = 120;

export const AIRPORT_TRANSFER_ID = 'airport_transfer';
export const STATION_TRANSFER_ID = 'station_transfer';

export const isTransferService = (serviceId: string): boolean =>
  serviceId === AIRPORT_TRANSFER_ID || serviceId === STATION_TRANSFER_ID;

const round2 = (n: number): number => Math.round(n * 100) / 100;

const toMinutes = (value?: string): number | null => {
  const m = String(value || '').match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const mi = parseInt(m[2], 10);
  if (h > 23 || mi > 59) return null;
  return h * 60 + mi;
};

/** L'HH:MM dentro una stringa qualsiasi ("2026-08-15T23:40" → "23:40"). */
export const transferHHMM = (value?: string): string => {
  const m = String(value || '').match(/(\d{2}:\d{2})/);
  return m ? m[1] : '';
};

/** Sottrae minuti a un "HH:MM", con wrap a mezzanotte. */
export const shiftHHMM = (value: string | undefined, minutes: number): string => {
  const m = String(value || '').match(/(\d{2}):(\d{2})/);
  if (!m) return '';
  let total = (parseInt(m[1], 10) * 60 + parseInt(m[2], 10) - minutes) % 1440;
  if (total < 0) total += 1440;
  const hh = String(Math.floor(total / 60)).padStart(2, '0');
  const mm = String(total % 60).padStart(2, '0');
  return `${hh}:${mm}`;
};

/** Finestra [start, end): start incluso, end escluso, wrap a mezzanotte. */
export const isWithinNightWindow = (hhmm: string, start?: string, end?: string): boolean => {
  const t = toMinutes(hhmm);
  const s = toMinutes(start);
  const e = toMinutes(end);
  if (t === null || s === null || e === null || s === e) return false;
  if (s < e) return t >= s && t < e;
  return t >= s || t < e;
};

/** Prezzo della stazione scelta (0 se non è fra quelle a listino). */
export const stationPrice = (opts: TransferOptions, name?: string): number => {
  const wanted = String(name || '').trim();
  if (!wanted) return 0;
  const list = opts.stationOptions || [];
  for (let i = 0; i < list.length; i++) {
    if (String(list[i]?.name || '').trim() === wanted) return Number(list[i]?.price) || 0;
  }
  return 0;
};

/** L'orario su cui si valuta la fascia notturna, tratta per tratta. */
export const transferLegTime = (
  serviceId: string,
  sel: TransferSelection,
  leg: 'arrival' | 'departure',
): string => {
  if (serviceId === AIRPORT_TRANSFER_ID) {
    if (leg === 'arrival') return transferHHMM(sel.arrivalDateTime);
    return shiftHHMM(transferHHMM(sel.departureDateTime), LEAVE_BEFORE_DEPARTURE_MIN);
  }
  return leg === 'arrival' ? String(sel.arrivalTime || '') : String(sel.departureTime || '');
};

/**
 * Il prezzo del transfer per le tratte scelte.
 *
 * `maxPersonsPerTransfer` NON moltiplica niente: è un'informazione ("max 4
 * persone per veicolo"), esattamente come sul sito.
 */
export function quoteTransfer(
  serviceId: string,
  opts: TransferOptions,
  sel: TransferSelection,
): TransferQuote {
  const airport = serviceId === AIRPORT_TRANSFER_ID;
  const station = serviceId === STATION_TRANSFER_ID;

  let arrivalAmount = 0;
  let departureAmount = 0;
  if (airport) {
    if (sel.isArrival) arrivalAmount = Number(opts.arrivalPrice) || 0;
    if (sel.isDeparture) departureAmount = Number(opts.departurePrice) || 0;
  } else if (station) {
    if (sel.isArrival) arrivalAmount = stationPrice(opts, sel.stationName);
    if (sel.isDeparture) departureAmount = stationPrice(opts, sel.departureStationName);
  }

  // Una tratta di stazione senza stazione scelta (o a 0 €) non è prezzata:
  // non entra nel totale e non prende il supplemento notturno.
  const arrivalPriced = !!sel.isArrival && (airport || arrivalAmount > 0);
  const departurePriced = !!sel.isDeparture && (airport || departureAmount > 0);

  let nightSurcharge = 0;
  const amount = Number(opts.nightSurchargeAmount) || 0;
  if (opts.nightSurchargeEnabled && amount > 0) {
    if (
      arrivalPriced
      && isWithinNightWindow(transferLegTime(serviceId, sel, 'arrival'), opts.nightSurchargeStart, opts.nightSurchargeEnd)
    ) {
      nightSurcharge += amount;
    }
    if (
      departurePriced
      && isWithinNightWindow(transferLegTime(serviceId, sel, 'departure'), opts.nightSurchargeStart, opts.nightSurchargeEnd)
    ) {
      nightSurcharge += amount;
    }
  }

  return {
    total: round2(
      (arrivalPriced ? arrivalAmount : 0) + (departurePriced ? departureAmount : 0) + nightSurcharge,
    ),
    nightSurcharge: round2(nightSurcharge),
    trips: (arrivalPriced ? 1 : 0) + (departurePriced ? 1 : 0),
  };
}

/** Perché il form non è ancora completo (null = si può inviare). */
export type TransferFormError =
  | 'no_direction'
  | 'missing_arrival_time'
  | 'missing_departure_time'
  | 'missing_arrival_flight'
  | 'missing_departure_flight'
  | 'invalid_arrival_station'
  | 'invalid_departure_station'
  | 'arrival_date_out_of_range'
  | 'departure_date_out_of_range';

/** I giorni ammessi ("YYYY-MM-DD"): arrivo il giorno del check-in o il successivo, partenza il giorno del check-out o il successivo. */
export interface TransferDateLimits {
  arrivalMin?: string;
  arrivalMax?: string;
  departureMin?: string;
  departureMax?: string;
}

const dayPart = (value?: string): string => String(value || '').slice(0, 10);
const dayInRange = (day: string, min?: string, max?: string): boolean =>
  !/^\d{4}-\d{2}-\d{2}$/.test(day) || ((!min || day >= min) && (!max || day <= max));

/**
 * Gli stessi controlli che fa il server (validateTransferDetails): farli anche
 * qui non è ridondanza, è la differenza fra "il bottone non si accende finché
 * manca il numero del volo" e "paghi e poi ti dice di no".
 */
export function validateTransferSelection(
  serviceId: string,
  opts: TransferOptions,
  sel: TransferSelection,
  limits?: TransferDateLimits,
): TransferFormError | null {
  if (!sel.isArrival && !sel.isDeparture) return 'no_direction';

  if (serviceId === AIRPORT_TRANSFER_ID) {
    if (sel.isArrival) {
      if (!transferHHMM(sel.arrivalDateTime)) return 'missing_arrival_time';
      if (opts.requiresFlightInfo && !String(sel.flightNumber || '').trim()) return 'missing_arrival_flight';
      if (limits && !dayInRange(dayPart(sel.arrivalDateTime), limits.arrivalMin, limits.arrivalMax)) return 'arrival_date_out_of_range';
    }
    if (sel.isDeparture) {
      if (!transferHHMM(sel.departureDateTime)) return 'missing_departure_time';
      if (opts.requiresFlightInfo && !String(sel.departureFlightNumber || '').trim()) {
        return 'missing_departure_flight';
      }
      if (limits && !dayInRange(dayPart(sel.departureDateTime), limits.departureMin, limits.departureMax)) return 'departure_date_out_of_range';
    }
    return null;
  }

  if (serviceId === STATION_TRANSFER_ID) {
    const known = (name?: string) =>
      (opts.stationOptions || []).some((s) => String(s.name).trim() === String(name || '').trim());
    if (sel.isArrival) {
      if (!known(sel.stationName)) return 'invalid_arrival_station';
      if (toMinutes(sel.arrivalTime) === null) return 'missing_arrival_time';
    }
    if (sel.isDeparture) {
      if (!known(sel.departureStationName)) return 'invalid_departure_station';
      if (toMinutes(sel.departureTime) === null) return 'missing_departure_time';
    }
  }

  return null;
}

/**
 * Le tratte ancora ACQUISTABILI adesso: il termine (`minAdvanceDays`) lo
 * calcola il server e arriva in `arrivalOpen`/`departureOpen`, così l'app non
 * rifà i conti sui fusi orari.
 *
 * Se una sola delle due è aperta, il form mostra solo quella per l'acquisto;
 * oltre il termine tutte e due restano disponibili come RICHIESTA.
 */
export function canBuyTransfer(opts: TransferOptions, sel: TransferSelection): boolean {
  if (!sel.isArrival && !sel.isDeparture) return false;
  if (sel.isArrival && !opts.arrivalOpen) return false;
  if (sel.isDeparture && !opts.departureOpen) return false;
  return true;
}
